/**
 * Client-side system search index for CMS destinations.
 * Destination catalog plus helpers to attach U5 live-entity hits.
 */

import type { AdminMe, AdminSearchHit } from "@vital/shared";
import { canPerform } from "@vital/shared";
import { NAV_SECTIONS, type NavItem, type NavSection } from "../config/nav";
import { cmsStrings } from "../terminology";

export type SystemSearchHit = {
  id: string;
  to: string;
  label: string;
  group: string;
  nested?: boolean;
};

/** Extra operator terms (Dari + English technical) keyed by destination. */
const ALIASES_BY_TO: Record<string, string[]> = {
  "/": ["home", "dashboard", "overview", "خانه", "داشبورد", "نمای کلی"],
  "/announcements": [
    "announcement",
    "notice",
    "emergency",
    "اطلاعیه",
    "اطلاعیه‌ها",
    "اعلان",
    "اورژانس",
  ],
  "/promotions": ["promotion", "offer", "cta", "پیشنهاد", "پیشنهادها", "پروموشن"],
  "/sponsored": [
    "sponsored",
    "advertiser",
    "campaign",
    "ad",
    "تبلیغ",
    "تبلیغات ویژه",
    "کمپین",
    "تبلیغ‌کننده",
  ],
  "/sites": [
    "site",
    "group",
    "hotspot",
    "سایت",
    "سایت‌ها",
    "گروه",
    "گروه سایت",
    "هات‌اسپات",
  ],
  "/reseller-profiles": [
    "reseller",
    "branding",
    "logo",
    "نماینده",
    "ریسلر",
    "برندینگ",
    "پروفایل نماینده",
  ],
  "/portal-bundles": ["bundle", "portal", "template", "باندل", "قالب", "فایل نصب"],
  "/fleet": ["fleet", "deploy", "inventory", "ناوگان", "استقرار", "موجودی"],
  "/fleet?tab=drift": ["drift", "mismatch", "ناهماهنگی", "ناهماهنگی تنظیمات", "درافت"],
  "/versions": [
    "version",
    "publish history",
    "rollback",
    "نسخه",
    "انتشار",
    "تاریخچه انتشار",
    "بازگردانی",
  ],
  "/observability": ["ops", "health", "monitor", "پایش", "سلامت", "مانیتور", "پایش عملیات"],
  "/observability?tab=alerts": ["alert", "هشدار", "هشدارها", "آلارم"],
  "/?section=activity": ["activity", "audit feed", "فعالیت", "رویداد"],
  "/observability?tab=backup": [
    "backup",
    "restore",
    "drill",
    "پشتیبان",
    "بازگردانی",
    "تمرین بازیابی",
  ],
  "/memberships": ["member", "invite", "عضویت", "اعضای سازمان", "دعوت", "عضو"],
  "/organizations": [
    "organization",
    "tenant",
    "حساب سازمان",
    "حساب‌های سازمان",
    "سازمان",
    "ایجاد سازمان",
  ],
  "/entitlements": ["plan", "quota", "entitlement", "پلان", "سهمیه", "دسترسی پلان"],
  "/users": ["user", "role", "mfa", "کاربر", "کاربران", "نقش", "ام‌اف‌ای"],
  "/settings": [
    "settings",
    "cta",
    "allowlist",
    "kill switch",
    "kill",
    "تنظیمات",
    "قطع فوری",
    "قطع فوری محتوای cms",
  ],
  "/audit": ["audit", "log", "history", "ممیزی", "سابقه", "سابقه تغییرات", "لاگ"],
};

const GROUP_ORDER = NAV_SECTIONS.map((section) => section.title);

/**
 * Deep-links kept out of the rail but still searchable (owner GO 2026-09-16).
 * Paths match CMS-UX-MASTER-PROGRAM query deep-links.
 */
const EXTRA_SEARCH_DESTINATIONS: Array<{
  to: string;
  label: string;
  group: string;
  permission?: string;
}> = [
  {
    to: "/fleet?tab=drift",
    label: cmsStrings.nav.drift,
    group: cmsStrings.groups.release,
    permission: "fleet.inventory.read",
  },
  {
    to: "/observability?tab=alerts",
    label: cmsStrings.nav.alerts,
    group: cmsStrings.groups.operations,
    permission: "audit.read",
  },
  {
    to: "/?section=activity",
    label: cmsStrings.nav.activity,
    group: cmsStrings.groups.overview,
  },
  {
    to: "/observability?tab=backup",
    label: cmsStrings.nav.backupRestore,
    group: cmsStrings.groups.operations,
    permission: "audit.read",
  },
];

export function normalizeSearchText(value: string): string {
  return value
    .normalize("NFKC")
    .replace(/ي/g, "ی")
    .replace(/ى/g, "ی")
    .replace(/ك/g, "ک")
    .replace(/[\u064B-\u065F\u0670]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function searchTokens(query: string): string[] {
  return normalizeSearchText(query).split(" ").filter(Boolean);
}

function visibleNavSections(user: Pick<AdminMe, "role" | "platformRole" | "membershipRole">): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (!item.permission) return true;
      return canPerform({
        platformRole: user.platformRole ?? null,
        membershipRole: user.membershipRole ?? null,
        legacyRole: user.role,
        permission: item.permission,
      });
    }),
  })).filter((section) => section.items.length > 0);
}

function haystackFor(item: NavItem, group: string): string {
  const aliases = ALIASES_BY_TO[item.to] ?? [];
  return normalizeSearchText([item.label, group, item.to, ...aliases].join(" "));
}

export function buildSystemSearchCatalog(
  user: Pick<AdminMe, "role" | "platformRole" | "membershipRole">,
): SystemSearchHit[] {
  const hits: SystemSearchHit[] = [];
  for (const section of visibleNavSections(user)) {
    for (const item of section.items) {
      hits.push({
        id: item.to,
        to: item.to,
        label: item.label,
        group: section.title,
        nested: item.nested,
      });
    }
  }
  for (const extra of EXTRA_SEARCH_DESTINATIONS) {
    if (extra.permission) {
      const allowed = canPerform({
        platformRole: user.platformRole ?? null,
        membershipRole: user.membershipRole ?? null,
        legacyRole: user.role,
        permission: extra.permission,
      });
      if (!allowed) continue;
    }
    hits.push({
      id: extra.to,
      to: extra.to,
      label: extra.label,
      group: extra.group,
      nested: true,
    });
  }
  return hits;
}

/** Empty-query browse: first item of each nav group, then fill remaining slots. */
export function previewSystemSearch(catalog: SystemSearchHit[], limit = 8): SystemSearchHit[] {
  const grouped = groupSystemSearchHits(catalog);
  const picked: SystemSearchHit[] = [];
  for (const group of grouped) {
    if (picked.length >= limit) break;
    const first = group.items[0];
    if (first) picked.push(first);
  }
  for (const group of grouped) {
    for (const item of group.items.slice(1)) {
      if (picked.length >= limit) return picked;
      picked.push(item);
    }
  }
  return picked;
}

export function matchSystemSearch(
  catalog: SystemSearchHit[],
  query: string,
  limit = 12,
): SystemSearchHit[] {
  const tokens = searchTokens(query);
  if (tokens.length === 0) {
    return previewSystemSearch(catalog, Math.min(limit, 8));
  }

  const scored: Array<{ hit: SystemSearchHit; score: number }> = [];
  for (const hit of catalog) {
    const hay = haystackFor(
      { to: hit.to, label: hit.label, nested: hit.nested },
      hit.group,
    );
    if (!tokens.every((token) => hay.includes(token))) continue;

    const label = normalizeSearchText(hit.label);
    const group = normalizeSearchText(hit.group);
    const aliases = (ALIASES_BY_TO[hit.to] ?? []).map(normalizeSearchText);
    const joined = tokens.join(" ");

    let score = 0;
    if (label.startsWith(joined)) score += 40;
    else if (label.includes(joined)) score += 25;
    if (aliases.some((alias) => alias === joined)) score += 30;
    else if (aliases.some((alias) => alias.startsWith(joined) || alias.includes(joined))) {
      score += 18;
    }
    if (group.includes(joined) || tokens.every((token) => group.includes(token))) score += 10;
    if (normalizeSearchText(hit.to).includes(joined)) score += 5;
    scored.push({ hit, score });
  }

  scored.sort((a, b) => b.score - a.score || a.hit.label.localeCompare(b.hit.label, "fa"));
  return scored.slice(0, limit).map((s) => s.hit);
}

export function groupSystemSearchHits(
  hits: SystemSearchHit[],
): Array<{ group: string; items: SystemSearchHit[] }> {
  const map = new Map<string, SystemSearchHit[]>();
  for (const hit of hits) {
    const list = map.get(hit.group) ?? [];
    list.push(hit);
    map.set(hit.group, list);
  }
  const ordered = GROUP_ORDER.filter((group) => map.has(group));
  for (const group of map.keys()) {
    if (!ordered.includes(group)) ordered.push(group);
  }
  return ordered.map((group) => ({ group, items: map.get(group)! }));
}

export function entityHitsToSystemHits(items: AdminSearchHit[]): SystemSearchHit[] {
  return items.map((item) => ({
    id: `record:${item.type}:${item.id}`,
    to: item.href,
    label: item.title,
    group: cmsStrings.shell.systemSearchRecords,
  }));
}
